import {useState, useMemo, useEffect} from 'react'
import {mapToObj, sortBy} from 'remeda'
import deepEqual from 'fast-deep-equal'

const listString = (rs: {id: string}[]) => rs.map(r => r.id).join('|')

export const useStickyOrder = <T extends {id: string}, U = T[]>(
  items: T[] | undefined,
  options?: {postOrderTransform?: (i: T[]) => U; disabled?: boolean}
) => {
  const {postOrderTransform, disabled} = options ?? {}
  const [itemsOrder, setItemsOrder] = useState<Record<string, number> | undefined>(undefined)
  const [showRefreshOrder, setShowRefreshOrder] = useState(false)

  // evaluate what the current order would be
  const sortedItems = useMemo(
    () =>
      items == null
        ? undefined
        : sortBy(
            items.map((r, i) => ({r, i})),
            r => itemsOrder?.[r.r.id] ?? Number.MAX_SAFE_INTEGER, // sort by explicit order
            r => r.i // then be the order it came in
          ).map(r => r.r),
    [items, itemsOrder]
  )

  // show button when order changes
  const sortedListString = listString(sortedItems ?? [])
  const rawListString = listString(items ?? [])
  useEffect(() => {
    if (disabled || !items) return
    setShowRefreshOrder(sortedListString != rawListString) // show if the resulting order would be different from the current order
  }, [sortedListString, rawListString])

  // set order on first load, when it is reset, or if items are added and the list doesn't change order
  useEffect(() => {
    if (!disabled && (!itemsOrder || sortedListString == rawListString) && sortedItems) {
      const newOrder = mapToObj(sortedItems, (r, i) => [r.id, i])
      if (!deepEqual(newOrder, itemsOrder)) {
        setItemsOrder(newOrder)
      }
    }
  }, [sortedItems, itemsOrder, disabled])

  // transform the order to the desired format
  const data = useMemo(() => {
    if (!sortedItems) return undefined
    return postOrderTransform ? postOrderTransform(sortedItems) : (sortedItems as any as U)
  }, [sortedItems])

  const reset = () => {
    setItemsOrder(undefined)
    setShowRefreshOrder(false)
  }

  return {data, showRefreshOrder, reset}
}

export const useStickyOrderRequired = <T extends {id: string}, U = T[]>(
  items: T[],
  options?: {postOrderTransform?: (i: T[]) => U; disabled?: boolean}
): {
  data: T[]
  showRefreshOrder: boolean
  reset: () => void
} => useStickyOrder(items, options) as any
